import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { StationAlias } from './StationAlias';
import { StationRole } from './StationRole';
import type { EntityState } from './../enums/EntityState';

export class StationDetails extends BaseObj {
    public ID: number;
    public __id: number;

    public BackupFor: number | null;
    public __backupfor: number | null;

    public BackupForStationID: number | null;
    public __backupforstationid: number | null;

    public readonly CameraUrl: string | null;

    public EventID: number;
    public __eventid: number;

    public Latitude: number | null;
    public __latitude: number | null;

    public Longitude: number | null;
    public __longitude: number | null;

    public Name: string | null;
    public __name: string | null;

    public ResponsibleEmail: string | null;
    public __responsibleemail: string | null;

    public ResponsibleName: string | null;
    public __responsiblename: string | null;

    public ResponsiblePhone: string | null;
    public __responsiblephone: string | null;

    public SortOrder: number;
    public __sortorder: number;

    public readonly StationAlias: string | null;

    public StationAliases: StationAlias[];
    public __stationaliases: StationAlias[];

    public StationRoles: StationRole[];
    public __stationroles: StationRole[];

    public StationTypeID: number | null;
    public __stationtypeid: number | null;

    public UsedTimeSetsStarttime: boolean;
    public __usedtimesetsstarttime: boolean;

    public UseUsedTime: boolean;
    public __useusedtime: boolean;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        BackupFor?: number | null;
        BackupForStationID?: number | null;
        CameraUrl?: string | null;
        EntityState?: EntityState;
        EventID?: number;
        Latitude?: number | null;
        Longitude?: number | null;
        Name?: string | null;
        ResponsibleEmail?: string | null;
        ResponsibleName?: string | null;
        ResponsiblePhone?: string | null;
        SortOrder?: number;
        StationAlias?: string | null;
        StationAliases?: StationAlias[];
        StationRoles?: StationRole[];
        StationTypeID?: number | null;
        UsedTimeSetsStarttime?: boolean;
        UseUsedTime?: boolean;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<StationDetails>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('StationDetails tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.BackupFor = values.BackupFor ?? null;
        this.__backupfor = values.BackupFor ?? null;
        Object.defineProperty(this, 'BackupFor', {
            get(): number | null {
                return this.__backupfor;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('BackupFor', this.__backupfor, value);
                this.__backupfor = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.BackupForStationID = values.BackupForStationID ?? null;
        this.__backupforstationid = values.BackupForStationID ?? null;
        Object.defineProperty(this, 'BackupForStationID', {
            get(): number | null {
                return this.__backupforstationid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('BackupForStationID', this.__backupforstationid, value);
                this.__backupforstationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CameraUrl = values.CameraUrl ?? null;

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Latitude = values.Latitude ?? null;
        this.__latitude = values.Latitude ?? null;
        Object.defineProperty(this, 'Latitude', {
            get(): number | null {
                return this.__latitude;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Latitude', this.__latitude, value);
                this.__latitude = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Longitude = values.Longitude ?? null;
        this.__longitude = values.Longitude ?? null;
        Object.defineProperty(this, 'Longitude', {
            get(): number | null {
                return this.__longitude;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Longitude', this.__longitude, value);
                this.__longitude = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? null;
        this.__name = values.Name ?? null;
        Object.defineProperty(this, 'Name', {
            get(): string | null {
                return this.__name;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ResponsibleEmail = values.ResponsibleEmail ?? null;
        this.__responsibleemail = values.ResponsibleEmail ?? null;
        Object.defineProperty(this, 'ResponsibleEmail', {
            get(): string | null {
                return this.__responsibleemail;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ResponsibleEmail', this.__responsibleemail, value);
                this.__responsibleemail = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ResponsibleName = values.ResponsibleName ?? null;
        this.__responsiblename = values.ResponsibleName ?? null;
        Object.defineProperty(this, 'ResponsibleName', {
            get(): string | null {
                return this.__responsiblename;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ResponsibleName', this.__responsiblename, value);
                this.__responsiblename = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ResponsiblePhone = values.ResponsiblePhone ?? null;
        this.__responsiblephone = values.ResponsiblePhone ?? null;
        Object.defineProperty(this, 'ResponsiblePhone', {
            get(): string | null {
                return this.__responsiblephone;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ResponsiblePhone', this.__responsiblephone, value);
                this.__responsiblephone = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SortOrder = values.SortOrder ?? 0;
        this.__sortorder = values.SortOrder ?? 0;
        Object.defineProperty(this, 'SortOrder', {
            get(): number {
                return this.__sortorder;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('SortOrder', this.__sortorder, value);
                this.__sortorder = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationAlias = values.StationAlias ?? null;

        this.StationAliases = values.StationAliases ?? [];
        this.__stationaliases = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.StationAliases ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('StationAliases', newVal),
        });
        this.__stationaliases.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('StationAliases', this.StationAliases),
            );
        });
        Object.defineProperty(this, 'StationAliases', {
            get(): StationAlias[] {
                return this.__stationaliases;
            },
            set(value: StationAlias[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__stationaliases = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: StationAlias[], newVal: StationAlias[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('StationAliases', newVal),
                });
                this.__stationaliases.forEach((child: StationAlias) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('StationAliases', this.StationAliases),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('StationAliases', this.StationAliases);
            },
            enumerable: true,
            configurable: true,
        });

        this.StationRoles = values.StationRoles ?? [];
        this.__stationroles = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.StationRoles ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('StationRoles', newVal),
        });
        this.__stationroles.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('StationRoles', this.StationRoles),
            );
        });
        Object.defineProperty(this, 'StationRoles', {
            get(): StationRole[] {
                return this.__stationroles;
            },
            set(value: StationRole[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__stationroles = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: StationRole[], newVal: StationRole[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('StationRoles', newVal),
                });
                this.__stationroles.forEach((child: StationRole) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('StationRoles', this.StationRoles),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('StationRoles', this.StationRoles);
            },
            enumerable: true,
            configurable: true,
        });

        this.StationTypeID = values.StationTypeID ?? null;
        this.__stationtypeid = values.StationTypeID ?? null;
        Object.defineProperty(this, 'StationTypeID', {
            get(): number | null {
                return this.__stationtypeid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('StationTypeID', this.__stationtypeid, value);
                this.__stationtypeid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.UsedTimeSetsStarttime = values.UsedTimeSetsStarttime ?? false;
        this.__usedtimesetsstarttime = values.UsedTimeSetsStarttime ?? false;
        Object.defineProperty(this, 'UsedTimeSetsStarttime', {
            get(): boolean {
                return this.__usedtimesetsstarttime;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('UsedTimeSetsStarttime', this.__usedtimesetsstarttime, value);
                this.__usedtimesetsstarttime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.UseUsedTime = values.UseUsedTime ?? false;
        this.__useusedtime = values.UseUsedTime ?? false;
        Object.defineProperty(this, 'UseUsedTime', {
            get(): boolean {
                return this.__useusedtime;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('UseUsedTime', this.__useusedtime, value);
                this.__useusedtime = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"BackupFor": ${JSON.stringify(this.BackupFor)}` +
            ',' +
            `"BackupForStationID": ${JSON.stringify(this.BackupForStationID)}` +
            ',' +
            `"CameraUrl": ${JSON.stringify(this.CameraUrl)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Latitude": ${JSON.stringify(this.Latitude)}` +
            ',' +
            `"Longitude": ${JSON.stringify(this.Longitude)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"ResponsibleEmail": ${JSON.stringify(this.ResponsibleEmail)}` +
            ',' +
            `"ResponsibleName": ${JSON.stringify(this.ResponsibleName)}` +
            ',' +
            `"ResponsiblePhone": ${JSON.stringify(this.ResponsiblePhone)}` +
            ',' +
            `"SortOrder": ${JSON.stringify(this.SortOrder)}` +
            ',' +
            `"StationAlias": ${JSON.stringify(this.StationAlias)}` +
            ',' +
            `"StationAliases": [ ${this.StationAliases.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"StationRoles": [ ${this.StationRoles.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"StationTypeID": ${JSON.stringify(this.StationTypeID)}` +
            ',' +
            `"UsedTimeSetsStarttime": ${JSON.stringify(this.UsedTimeSetsStarttime)}` +
            ',' +
            `"UseUsedTime": ${JSON.stringify(this.UseUsedTime)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of StationDetails from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        BackupFor?: number | null;
        BackupForStationID?: number | null;
        EventID?: number;
        Latitude?: number | null;
        Longitude?: number | null;
        Name?: string | null;
        ResponsibleEmail?: string | null;
        ResponsibleName?: string | null;
        ResponsiblePhone?: string | null;
        SortOrder?: number;
        StationAliases?: StationAlias[];
        StationRoles?: StationRole[];
        StationTypeID?: number | null;
        UsedTimeSetsStarttime?: boolean;
        UseUsedTime?: boolean;
        EntityState?: 'Added' | 'Unchanged';
    }): StationDetails {
        return new StationDetails(values);
    }

    public static _FromServerJson(json: any): StationDetails {
        return new StationDetails({
            ID: json.ID,
            BackupFor: json.BackupFor ?? null,
            BackupForStationID: json.BackupForStationID ?? null,
            CameraUrl: json.CameraUrl ?? '',
            EventID: json.EventID,
            Latitude: json.Latitude ?? null,
            Longitude: json.Longitude ?? null,
            Name: json.Name ?? '',
            ResponsibleEmail: json.ResponsibleEmail ?? '',
            ResponsibleName: json.ResponsibleName ?? '',
            ResponsiblePhone: json.ResponsiblePhone ?? '',
            SortOrder: json.SortOrder,
            StationAlias: json.StationAlias ?? '',
            StationAliases: json.StationAliases?.map((x: any) => StationAlias._FromServerJson(x)),
            StationRoles: json.StationRoles?.map((x: any) => StationRole._FromServerJson(x)),
            StationTypeID: json.StationTypeID ?? null,
            UsedTimeSetsStarttime: json.UsedTimeSetsStarttime,
            UseUsedTime: json.UseUsedTime,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of StationDetails from another instance of StationDetails.
     * @param other The StationDetails you want to clone
     * @returns A cloned StationDetails
     */
    public static FromOther(other: StationDetails): StationDetails {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return StationDetails._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return StationDetails.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'BackupFor'
            | 'BackupForStationID'
            | 'CameraUrl'
            | 'EventID'
            | 'Latitude'
            | 'Longitude'
            | 'Name'
            | 'ResponsibleEmail'
            | 'ResponsibleName'
            | 'ResponsiblePhone'
            | 'SortOrder'
            | 'StationAlias'
            | 'StationAliases'
            | 'StationRoles'
            | 'StationTypeID'
            | 'UsedTimeSetsStarttime'
            | 'UseUsedTime'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'StationDetails';
}
